import {
  APICallState,
  CountryDropdown,
  FormField,
  PanelContainer,
  ProvinceDropdown,
  StateDropdown,
  useCountry,
  useIsDesktop,
  useProvince,
  useStateUS,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {yupResolver} from '@hookform/resolvers/yup';
import PersonalInfoIcon from 'assets/ProfileEmptyStateIcons/icon_personal_info.svg';
import {Flex, View} from 'native-base';
import React, {useEffect, useMemo} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {useWindowDimensions} from 'react-native';

import ProfileEmptyStateModule from 'components/ProfileEmptyState/ProfileEmptyStateModule';
import {useMaskPhoneNumber} from 'hooks/useMaskPhoneNumber';
import {useMaskZip} from 'hooks/useMaskZip';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';
import {NORTH_AMERICA_CODE, PersonalInfo} from 'models/Profile';
import {PersonalInfoModuleValidation} from 'validation/module/PersonalInfoValidation';

export interface PersonalInfoModuleProps {
  address?: string;
  address2?: string;
  cellPhone?: string;
  city?: string;
  country?: string;
  email?: string;
  homePhone?: string;
  stateprovince?: string;
  zip?: string;
  handleSaveProfileInfo: (data: PersonalInfo) => void;
  isPanelUnlocked: boolean;
  onEdit: () => void;
  stopEditing: () => void;
  profileInfoState: APICallState;
  onOverlayAnimationFinish: () => void;
  isEditLoading: boolean;
}

const TEST_ID_PREFIX = 'profile-personal-info-module';

export const getZipCode = ({isUSA, zip}: {isUSA: boolean; zip: string}) =>
  isUSA ? zip.replace('-', '') : zip || '00000';

const PersonalInfoModule = (props: PersonalInfoModuleProps) => {
  const {
    address,
    address2,
    cellPhone,
    city,
    country,
    email,
    homePhone,
    stateprovince,
    zip,
    handleSaveProfileInfo,
    isPanelUnlocked,
    onEdit,
    stopEditing,
    profileInfoState,
    onOverlayAnimationFinish,
    isEditLoading,
  } = props;

  const isDesktop = useIsDesktop();
  const {height} = useWindowDimensions();
  const dropdownMaxHeight = isDesktop ? `${height / 2}px` : `${height / 4}px`;
  const countryDisplay = useCountry(country ?? '');
  const stateDisplay = useStateUS(stateprovince ?? '');
  const provinceDisplay = useProvince(stateprovince ?? '');

  const {maskNumber, unmaskNumber} = useMaskPhoneNumber();

  const defaultValues = useMemo(
    () => ({
      address,
      address2,
      city,
      country: countryDisplay
        ? {display: countryDisplay, code: country ?? ''}
        : undefined,
      email,
      state: stateDisplay
        ? {
            display: stateDisplay,
            code: stateprovince || '',
          }
        : undefined,
      province: provinceDisplay
        ? {
            display: provinceDisplay,
            code: stateprovince || '',
          }
        : undefined,
      zip,
      homePhone,
      cellPhone,
    }),
    [
      address,
      address2,
      cellPhone,
      city,
      country,
      countryDisplay,
      email,
      homePhone,
      provinceDisplay,
      stateDisplay,
      stateprovince,
      zip,
    ]
  );

  const {handleSubmit, control, formState, reset, watch, setValue} = useForm({
    defaultValues,
    resolver: yupResolver(PersonalInfoModuleValidation),
    mode: 'onBlur',
  });

  const watchCountry = watch('country');
  const watchState = watch('state');
  const watchProvince = watch('province');
  const isCanada = watchCountry?.code === 'CAN';
  const isUSA = watchCountry?.code === 'USA';
  const {errors} = formState;

  const maskZip = useMaskZip({isCanada, isUSA});

  // Reset the form if we are not editing - rerender for loading finished
  useEffect(() => {
    if (isPanelUnlocked) {
      return;
    }

    reset(defaultValues);
  }, [isPanelUnlocked, reset, defaultValues]);

  // Display helpers
  const ehc = email || homePhone || cellPhone || isPanelUnlocked;
  const csz = city || stateprovince || zip || isPanelUnlocked;
  const cacsz = countryDisplay || address || address2 || csz || isPanelUnlocked;
  const any = ehc || cacsz || isPanelUnlocked;

  const {trackAdobeEvent} = useTrackEvent();

  const onEditPress = () => {
    onEdit();
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {
        name: 'edit personal information',
      },
    });
  };

  const onCancelPress = () => {
    stopEditing();
    reset(defaultValues);
  };

  const onSavePress = () => {
    handleSubmit(data => {
      handleSaveProfileInfo({
        email: data.email,
        homephone: unmaskNumber(data.homePhone),
        homeaddress1: data.address,
        homeaddress2: data.address2,
        mobilenumber: unmaskNumber(data.cellPhone),
        homestate: isUSA ? data.state?.code : data.province?.code,
        homecountry: data.country?.code,
        countrycode: NORTH_AMERICA_CODE, // This is country code for the provided cell number, defaulting to North America for now
        homezip: getZipCode({isUSA, zip: data.zip || ''}),
        homecity: data.city,
      });
    })();
  };

  const emptyState = (
    <ProfileEmptyStateModule
      onCTAPress={onEditPress}
      svgIcon={PersonalInfoIcon}
      ctaTitle="Add Personal Information"
      body="You have no personal information on file."
      emptyStateTitle="ADD PERSONAL INFORMATION"
      emptyStateIcon="user-circle"
    />
  );

  if (!any && !isPanelUnlocked) {
    return emptyState;
  }

  return (
    <PanelContainer
      isLoading={isEditLoading}
      testID="personal-info-panel"
      icon="user-circle"
      panelTitle="Personal Information"
      contentMaxWidth="100%"
      showEdit={!isPanelUnlocked && !!any}
      isSaving={profileInfoState === APICallState.LOADING}
      onEdit={onEditPress}
      onCancel={onCancelPress}
      onSave={onSavePress}
      apiCallState={profileInfoState}
      hasFooter={isPanelUnlocked}
      onOverlayAnimationFinish={onOverlayAnimationFinish}>
      <Flex mt={2} flexDirection={isDesktop ? 'row' : 'column'} width="100%">
        {!ehc ? null : (
          <View
            minW="50%"
            paddingRight={isDesktop ? 6 : 0}
            marginBottom={isDesktop ? 0 : 4}>
            {!email && !isPanelUnlocked ? null : (
              <View mb={isDesktop ? '40px' : 4}>
                <Controller
                  name="email"
                  control={control}
                  render={({field}) => (
                    <FormField
                      nativeID={`${TEST_ID_PREFIX}-email`}
                      testID={`${TEST_ID_PREFIX}-email`}
                      label={'EMAIL ADDRESS'}
                      editable={isPanelUnlocked}
                      isInvalid={!!errors?.email}
                      isInvalidLabel={errors?.email?.message}
                      onChangeText={field.onChange}
                      onBlur={field.onBlur}
                      value={field.value}
                    />
                  )}
                />
              </View>
            )}
            {!homePhone && !isPanelUnlocked ? null : (
              <View mb={isDesktop ? '40px' : null}>
                <Controller
                  name="homePhone"
                  control={control}
                  render={({field}) => (
                    <FormField
                      nativeID={`${TEST_ID_PREFIX}-home-phone`}
                      testID={`${TEST_ID_PREFIX}-home-phone`}
                      label="HOME PHONE"
                      editable={isPanelUnlocked}
                      isInvalid={!!errors.homePhone}
                      isInvalidLabel={errors?.homePhone?.message}
                      onChangeText={text => field.onChange(maskNumber(text))}
                      onBlur={field.onBlur}
                      value={field.value}
                    />
                  )}
                />
              </View>
            )}
            {!cellPhone && !isPanelUnlocked ? null : (
              <View>
                <Controller
                  name="cellPhone"
                  control={control}
                  render={({field}) => (
                    <FormField
                      nativeID={`${TEST_ID_PREFIX}-cell-phone`}
                      testID={`${TEST_ID_PREFIX}-cell-phone`}
                      label="CELLPHONE"
                      editable={isPanelUnlocked}
                      isInvalid={!!errors.cellPhone}
                      isInvalidLabel={errors?.cellPhone?.message}
                      onChangeText={text => field.onChange(maskNumber(text))}
                      onBlur={field.onBlur}
                      value={field.value}
                    />
                  )}
                />
              </View>
            )}
          </View>
        )}
        {!cacsz ? null : (
          <View
            pl={ehc && isDesktop ? 6 : undefined}
            borderLeftColor={
              ehc && isDesktop ? 'rgba(112, 119, 132, .2)' : undefined
            }
            borderLeftWidth={ehc && isDesktop ? 1 : undefined}
            flexGrow={1}
            flexShrink={1}>
            {!countryDisplay && !isPanelUnlocked ? null : (
              <View mb={isDesktop ? '40px' : 4}>
                <CountryDropdown
                  maxHeight={dropdownMaxHeight}
                  editable={isPanelUnlocked}
                  onSelected={value => {
                    setValue('country', value);
                    setValue('zip', '');
                  }}
                  value={watchCountry}
                />
              </View>
            )}
            {!address && !isPanelUnlocked ? null : (
              <View mb={isDesktop ? '40px' : 4}>
                <Controller
                  name="address"
                  control={control}
                  render={({field}) => (
                    <FormField
                      nativeID={`${TEST_ID_PREFIX}-home-address-1`}
                      testID={`${TEST_ID_PREFIX}-home-address-1`}
                      label="HOME ADDRESS"
                      editable={isPanelUnlocked}
                      isInvalid={!!errors?.address}
                      isInvalidLabel={errors?.address?.message}
                      onChangeText={field.onChange}
                      value={field.value}
                      onBlur={field.onBlur}
                    />
                  )}
                />
              </View>
            )}
            {!address2 && !isPanelUnlocked ? null : (
              <View mb={isDesktop ? '40px' : 4}>
                <Controller
                  name="address2"
                  control={control}
                  render={({field}) => (
                    <FormField
                      nativeID={`${TEST_ID_PREFIX}-home-address-2`}
                      testID={`${TEST_ID_PREFIX}-home-address-2`}
                      label="HOME ADDRESS 2"
                      editable={isPanelUnlocked}
                      isInvalid={!!errors?.address2}
                      isInvalidLabel={errors?.address2?.message}
                      onChangeText={field.onChange}
                      onBlur={field.onBlur}
                      value={field.value}
                    />
                  )}
                />
              </View>
            )}
            {isDesktop ? null : (
              <View mb={4} flex={5}>
                <Controller
                  name="city"
                  control={control}
                  render={({field}) => (
                    <FormField
                      nativeID={`${TEST_ID_PREFIX}-city`}
                      testID={`${TEST_ID_PREFIX}-city`}
                      label="CITY"
                      editable={isPanelUnlocked}
                      isInvalid={!!errors?.city}
                      isInvalidLabel={errors?.city?.message}
                      onChangeText={field.onChange}
                      onBlur={field.onBlur}
                      value={field.value}
                    />
                  )}
                />
              </View>
            )}
            {!csz ? null : (
              <View>
                <Flex flexDirection="row">
                  {!isDesktop ? null : !city && !isPanelUnlocked ? null : (
                    <View flex={isDesktop ? 5 : undefined} mr={1}>
                      <Controller
                        name="city"
                        control={control}
                        render={({field}) => (
                          <FormField
                            nativeID={`${TEST_ID_PREFIX}-city`}
                            testID={`${TEST_ID_PREFIX}-city`}
                            label="CITY"
                            editable={isPanelUnlocked}
                            isInvalid={!!errors?.city}
                            isInvalidLabel={errors?.city?.message}
                            onChangeText={field.onChange}
                            onBlur={field.onBlur}
                            value={field.value}
                          />
                        )}
                      />
                    </View>
                  )}
                  {(!stateprovince && !isPanelUnlocked) ||
                  (!isUSA && !isCanada) ? null : (
                    <View mr={isDesktop ? 6 : 5} maxW="100px" flex={1}>
                      {isUSA ? (
                        <StateDropdown
                          maxHeight={dropdownMaxHeight}
                          required={!!errors.state}
                          editable={isPanelUnlocked}
                          value={stateDisplay ? watchState : undefined}
                          onSelected={value => setValue('state', value)}
                        />
                      ) : isCanada ? (
                        <ProvinceDropdown
                          maxHeight={dropdownMaxHeight}
                          required={!!errors.province}
                          editable={isPanelUnlocked}
                          value={provinceDisplay ? watchProvince : undefined}
                          onSelected={value => setValue('province', value)}
                        />
                      ) : null}
                    </View>
                  )}
                  {!zip && !isPanelUnlocked ? null : (
                    <View flex={isDesktop ? 2 : undefined}>
                      <Controller
                        name="zip"
                        control={control}
                        render={({field}) => (
                          <FormField
                            maxLength={isUSA ? 10 : isCanada ? 7 : undefined}
                            nativeID={`${TEST_ID_PREFIX}-zip-code`}
                            testID={`${TEST_ID_PREFIX}-zip-code`}
                            label={isUSA ? 'ZIP CODE' : 'POSTAL CODE'}
                            editable={isPanelUnlocked}
                            isInvalid={!!errors?.zip}
                            isInvalidLabel={errors?.zip?.message}
                            onChangeText={text => {
                              field.onChange(maskZip(text));
                            }}
                            value={field.value}
                            onBlur={field.onBlur}
                          />
                        )}
                      />
                    </View>
                  )}
                </Flex>
              </View>
            )}
          </View>
        )}
      </Flex>
    </PanelContainer>
  );
};

export default PersonalInfoModule;
